import { Localization } from "@viamap/viamap2-common";
import JSZip from "jszip";
import jsPDF from "jspdf";
import { MouseEvent as ReactMouseEvent, useMemo, useState } from "react";
import { GlassButton } from "src/components/GlassButtons";
import { message } from "src/components/Message";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { DC } from "src/dataCollector/DataCollector";
import { ExportData } from "src/managers/ExportData";
import {
  buildDownloadFileName,
  getServimapDownloadFileName,
  getServitutBilagDownloadFileName,
  getServitutDownloadFileName,
} from "src/managers/ServitutDownloadNaming";
import {
  fetchTingbogDocumentBlob,
  getTingbogDownloadTarget,
  isTingbogElectronicDocument,
  isTingbogPdfEligibleByDate,
} from "src/managers/TingbogDownload";
import { Feature, FeatureType } from "src/states/ApplicationState";
import { ViewButtonIcon } from "./PropertyComponents";
import type { PropertyData } from "./PropertyInfoCollectorTypes";

type ServitutLike = NonNullable<
  NonNullable<PropertyData["ViamapTingBog"]>["servitutter"]
>[number];

type BilagReference = {
  ref: string;
  kode: string;
  beskrivelse: string;
  isLikelyGml: boolean;
};

type ServitutBatchPackageResult = {
  createdFiles: number;
  warnings: string[];
};

const SERVIMAP_LOGO_URL = new URL("/viamap-logo-sort.png", import.meta.url).href;
let servimapLogoDataUrlPromise: Promise<string | null> | null = null;

function normalizeWhitespace(value?: string | null): string {
  return (value || "").replace(/\s+/g, " ").trim();
}

function getTingBogRecordNumber(a: string) {
  return a?.replace(/(.{4})(.{2})(.{2})-(.*)/, "$3$2$1-$4") ?? "";
}

function getServitutSummaryText(servitut: {
  tekstSummarisk?: string;
  tekster?: string[];
}) {
  return (
    servitut.tekstSummarisk ||
    (servitut.tekster && servitut.tekster.join(" ")) ||
    ""
  ).trim();
}

function getServitutMainDocumentFileName(servitut: ServitutLike): string {
  const datoloebenr = getTingBogRecordNumber(
    servitut.aktId || servitut.aktAliasId || "",
  );
  return getServitutDownloadFileName({
    datoloebenr,
    priorityNumber: servitut.prioritetNummer,
    headingText: getServitutSummaryText(servitut),
    fallbackStem:
      servitut.dokumentId ||
      servitut.dokumentFilnavn ||
      datoloebenr ||
      "servitut",
  });
}

function splitFileName(fileName?: string): {
  baseName: string;
  extension: string;
} {
  const trimmed = (fileName || "").trim();
  const match = /^(.*?)(\.[^.]+)?$/i.exec(trimmed);
  return {
    baseName: (match?.[1] || trimmed || "file").trim(),
    extension: match?.[2] || "",
  };
}

function getPropertyBfe(propertyData: Partial<PropertyData>): string {
  const value =
    propertyData?.BBRRel?.bfeNummer ??
    propertyData?.BBRSFE?.[0]?.properties?.BFEnummer ??
    propertyData?.ViamapEjendomData?.sag?.BFEnr ??
    propertyData?.GQL_BBR?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer ??
    "";
  return `${value || ""}`.trim();
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to read image data"));
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read image data"));
    reader.readAsDataURL(blob);
  });
}

async function loadServimapLogoDataUrl(): Promise<string | null> {
  if (!servimapLogoDataUrlPromise) {
    servimapLogoDataUrlPromise = fetch(SERVIMAP_LOGO_URL)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Logo request failed (${response.status})`);
        }
        return blobToDataUrl(await response.blob());
      })
      .catch((error) => {
        console.warn("[SERVIMAP] Could not load logo", error);
        return null;
      });
  }
  return servimapLogoDataUrlPromise;
}

function createUniqueZipEntryName(zip: JSZip, fileName: string): string {
  const { baseName, extension } = splitFileName(fileName);
  const safeBaseName = baseName || "file";
  let candidate = `${safeBaseName}${extension}`;
  let suffix = 2;
  while (zip.files[candidate]) {
    candidate = `${safeBaseName}_${suffix}${extension}`;
    suffix += 1;
  }
  return candidate;
}

function addFileToZip(zip: JSZip, fileName: string, data: Blob | string): string {
  const uniqueName = createUniqueZipEntryName(zip, fileName);
  zip.file(uniqueName, data);
  return uniqueName;
}

function isEligibleDigitalServitut(servitut: ServitutLike): boolean {
  return (
    isTingbogElectronicDocument(servitut) &&
    isTingbogPdfEligibleByDate(servitut.tinglysningsDato) &&
    Boolean(getTingbogDownloadTarget(servitut))
  );
}

function isLikelyGmlBilag(kode: string, beskrivelse: string): boolean {
  if (kode === "38") {
    return true;
  }
  return `${kode || ""} ${beskrivelse || ""}`.toLowerCase().includes("gml");
}

function extractBilagReferences(xmlDoc: Document): BilagReference[] {
  const samling = xmlDoc.getElementsByTagName("BilagReferenceSamling")[0];
  if (!samling) {
    return [];
  }

  return Array.from(samling.children)
    .map((entry) => {
      const ref =
        normalizeWhitespace(
          entry.getElementsByTagName("BilagReference")[0]?.textContent,
        ) || "";
      if (!ref) {
        return null;
      }
      const kode = normalizeWhitespace(
        entry.getElementsByTagName("BilagReferenceKode")[0]?.textContent,
      );
      const beskrivelse = normalizeWhitespace(
        entry.getElementsByTagName("Afsnit")[0]?.textContent,
      );
      return {
        ref,
        kode,
        beskrivelse,
        isLikelyGml: isLikelyGmlBilag(kode, beskrivelse),
      };
    })
    .filter((entry): entry is BilagReference => Boolean(entry));
}

function extractServitutText(xmlDoc: Document, servitut: ServitutLike): string {
  const servitutNode = xmlDoc.getElementsByTagName("Servitut")[0];
  if (!servitutNode) {
    return getServitutSummaryText(servitut);
  }

  const textGroups = Array.from(servitutNode.getElementsByTagName("TekstGruppe"));
  const blocks = textGroups
    .map((group) => {
      const header = normalizeWhitespace(
        Array.from(group.children).find((child) => child.tagName === "Overskrift")
          ?.textContent,
      );
      const paragraphs = Array.from(group.children)
        .filter((child) => child.tagName === "Afsnit")
        .map((child) => normalizeWhitespace(child.textContent))
        .filter(Boolean);

      return [header, ...paragraphs].filter(Boolean).join("\n");
    })
    .filter(Boolean);

  const joined = blocks.join("\n\n").trim();
  return joined || getServitutSummaryText(servitut);
}

function getAddressAccessKey(element?: Element | null): string {
  if (!element) {
    return "";
  }
  const municipalityCode = normalizeWhitespace(
    element.getElementsByTagName("MunicipalityCode")[0]?.textContent,
  );
  const streetCode = normalizeWhitespace(
    element.getElementsByTagName("StreetCode")[0]?.textContent,
  );
  const streetBuildingIdentifier = normalizeWhitespace(
    element.getElementsByTagName("StreetBuildingIdentifier")[0]?.textContent,
  );
  return [municipalityCode, streetCode, streetBuildingIdentifier]
    .filter(Boolean)
    .join("|");
}

function normalizeLookupKey(value?: string | null): string {
  return normalizeWhitespace(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "");
}

type SnapshotAddressInfo = {
  addressLine: string;
  postLine: string;
};

type SnapshotRoleInfo = SnapshotAddressInfo & {
  roleReference: string;
  name: string;
  cvrNumber: string;
};

type SnapshotPropertyInfo = SnapshotAddressInfo & {
  bfe: string;
  landsejerlav: string;
  matrikelnummer: string;
};

type PaataleContext = {
  rolesByReference: Map<string, SnapshotRoleInfo>;
  rolesByNameKey: Map<string, SnapshotRoleInfo>;
  addressesByKey: Map<string, SnapshotAddressInfo>;
  propertiesByBfe: Map<string, SnapshotPropertyInfo>;
};

function buildAddressInfo(addressElement?: Element | null): SnapshotAddressInfo | null {
  if (!addressElement) {
    return null;
  }
  const streetName = normalizeWhitespace(
    addressElement.getElementsByTagName("StreetName")[0]?.textContent,
  );
  const streetBuildingIdentifier = normalizeWhitespace(
    addressElement.getElementsByTagName("StreetBuildingIdentifier")[0]?.textContent,
  );
  const postCode = normalizeWhitespace(
    addressElement.getElementsByTagName("PostCodeIdentifier")[0]?.textContent,
  );
  const districtName = normalizeWhitespace(
    addressElement.getElementsByTagName("DistrictName")[0]?.textContent,
  );
  const addressLine = [streetName, streetBuildingIdentifier]
    .filter(Boolean)
    .join(" ");
  const postLine = [postCode, districtName].filter(Boolean).join(" ");

  if (!addressLine && !postLine) {
    return null;
  }

  return {
    addressLine,
    postLine,
  };
}

function buildPaataleContext(xmlDoc: Document): PaataleContext {
  const snapshotSamling = xmlDoc.getElementsByTagName("SnapshotSamling")[0];
  const addressesByKey = new Map<string, SnapshotAddressInfo>();
  const rolesByReference = new Map<string, SnapshotRoleInfo>();
  const rolesByNameKey = new Map<string, SnapshotRoleInfo>();
  const propertiesByBfe = new Map<string, SnapshotPropertyInfo>();

  if (!snapshotSamling) {
    return {
      rolesByReference,
      rolesByNameKey,
      addressesByKey,
      propertiesByBfe,
    };
  }

  Array.from(snapshotSamling.getElementsByTagName("AdresseStruktur")).forEach((address) => {
    const key = getAddressAccessKey(
      address.getElementsByTagName("AddressAccess")[0] || null,
    );
    const info = buildAddressInfo(address);
    if (key && info) {
      addressesByKey.set(key, info);
    }
  });

  Array.from(snapshotSamling.getElementsByTagName("RolleStruktur")).forEach((role) => {
    const roleReference = normalizeWhitespace(
      role.getElementsByTagName("RolleReference")[0]?.textContent,
    );
    if (!roleReference) {
      return;
    }
    const name = normalizeWhitespace(
      role.getElementsByTagName("Navn")[0]?.textContent,
    );
    const cvrNumber = normalizeWhitespace(
      role.getElementsByTagName("CVRnumberIdentifier")[0]?.textContent,
    );
    const addressKey = getAddressAccessKey(
      role.getElementsByTagName("AddressAccess")[0] || null,
    );
    const addressInfo =
      addressesByKey.get(addressKey) ||
      buildAddressInfo(role) ||
      ({ addressLine: "", postLine: "" } satisfies SnapshotAddressInfo);
    const info: SnapshotRoleInfo = {
      roleReference,
      name,
      cvrNumber,
      ...addressInfo,
    };
    rolesByReference.set(roleReference, info);
    const nameKey = normalizeLookupKey(name);
    if (nameKey) {
      rolesByNameKey.set(nameKey, info);
    }
  });

  Array.from(snapshotSamling.getElementsByTagName("EjendomStruktur")).forEach((property) => {
    const bfe = normalizeWhitespace(
      property.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent,
    );
    if (!bfe) {
      return;
    }
    const addressKey = getAddressAccessKey(
      property.getElementsByTagName("AddressAccess")[0] || null,
    );
    const addressInfo =
      addressesByKey.get(addressKey) ||
      buildAddressInfo(property) ||
      ({ addressLine: "", postLine: "" } satisfies SnapshotAddressInfo);
    const landsejerlav = normalizeWhitespace(
      property.getElementsByTagName("CadastralDistrictName")[0]?.textContent,
    );
    const matrikelnummer = normalizeWhitespace(
      property.getElementsByTagName("Matrikelnummer")[0]?.textContent,
    );
    propertiesByBfe.set(bfe, {
      bfe,
      landsejerlav,
      matrikelnummer,
      ...addressInfo,
    });
  });

  return {
    rolesByReference,
    rolesByNameKey,
    addressesByKey,
    propertiesByBfe,
  };
}

function formatRolePaataleBlock(role: SnapshotRoleInfo): string {
  return [
    role.name ? `Navn: ${role.name}` : "",
    role.addressLine,
    role.postLine,
    role.cvrNumber ? `Cvr-nr.: ${role.cvrNumber}` : "",
  ].filter(Boolean).join("\n");
}

function formatPropertyPaataleBlock(
  property: Partial<SnapshotPropertyInfo>,
  herskendeEjendom?: string | null,
): string {
  const resolvedHerskende = herskendeEjendom || "Nej";
  return [
    property.addressLine ? `Adresse: ${property.addressLine}` : "",
    property.postLine || "",
    property.landsejerlav ? `Landsejerlav: ${property.landsejerlav}` : "",
    property.matrikelnummer ? `Matrikelnummer: ${property.matrikelnummer}` : "",
    `Herskende ejendom: ${resolvedHerskende}`,
  ].filter(Boolean).join("\n");
}

function findRoleByName(
  context: PaataleContext,
  roleName: string,
): SnapshotRoleInfo | undefined {
  const nameKey = normalizeLookupKey(roleName);
  if (!nameKey) {
    return undefined;
  }
  const exact = context.rolesByNameKey.get(nameKey);
  if (exact) {
    return exact;
  }
  return Array.from(context.rolesByNameKey.entries()).find(([key]) =>
    key.includes(nameKey) || nameKey.includes(key),
  )?.[1];
}

function extractHerskendeEjendomValue(element?: Element | null): string | null {
  if (!element) {
    return null;
  }
  const nodes = [element, ...Array.from(element.getElementsByTagName("*"))];
  for (const node of nodes) {
    const tagKey = normalizeLookupKey(node.tagName);
    if (!tagKey.includes("herskende")) {
      continue;
    }
    const rawValue = normalizeWhitespace(node.textContent);
    if (!rawValue) {
      continue;
    }
    if (rawValue.toLowerCase() === "true") {
      return "Ja";
    }
    if (rawValue.toLowerCase() === "false") {
      return "Nej";
    }
    return rawValue;
  }
  return null;
}

function extractPropertyInfoFromElement(
  element: Element,
  context: PaataleContext,
): Partial<SnapshotPropertyInfo> | null {
  const bfe = normalizeWhitespace(
    element.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent,
  );
  const mappedProperty = bfe ? context.propertiesByBfe.get(bfe) : undefined;
  const addressKey = getAddressAccessKey(
    element.getElementsByTagName("AddressAccess")[0] || null,
  );
  const addressInfo =
    context.addressesByKey.get(addressKey) ||
    buildAddressInfo(element) ||
    mappedProperty ||
    null;
  const landsejerlav =
    normalizeWhitespace(
      element.getElementsByTagName("CadastralDistrictName")[0]?.textContent,
    ) ||
    mappedProperty?.landsejerlav ||
    "";
  const matrikelnummer =
    normalizeWhitespace(
      element.getElementsByTagName("Matrikelnummer")[0]?.textContent,
    ) ||
    mappedProperty?.matrikelnummer ||
    "";

  if (!addressInfo && !landsejerlav && !matrikelnummer) {
    return null;
  }

  return {
    addressLine: addressInfo?.addressLine || "",
    postLine: addressInfo?.postLine || "",
    landsejerlav,
    matrikelnummer,
  };
}

function extractPaataleRoleBlocks(
  xmlDoc: Document,
  context: PaataleContext,
): string[] {
  const anmeldelseInformation = xmlDoc.getElementsByTagName(
    "ElektroniskaktAnmeldelseInformation",
  )[0];
  if (!anmeldelseInformation) {
    return [];
  }

  return Array.from(anmeldelseInformation.getElementsByTagName("Rolle"))
    .filter((role) => {
      const roleType = normalizeLookupKey(
        role.getElementsByTagName("RolleTypeIdentifikator")[0]?.textContent,
      );
      return roleType === "paataleberettiget";
    })
    .map((role) => {
      const roleId = normalizeWhitespace(role.getAttribute("id"));
      const snapshotRole = roleId
        ? context.rolesByReference.get(roleId)
        : undefined;
      if (snapshotRole) {
        return formatRolePaataleBlock(snapshotRole);
      }

      const cvrNumber = normalizeWhitespace(
        role.getElementsByTagName("CVRnumberIdentifier")[0]?.textContent,
      );
      return cvrNumber ? `Cvr-nr.: ${cvrNumber}` : "";
    })
    .filter(Boolean);
}

function extractStructuredPaataleBlocks(
  xmlDoc: Document,
  context: PaataleContext,
): string[] {
  const blocks = new Set<string>();
  const candidates = Array.from(xmlDoc.getElementsByTagName("*")).filter((element) => {
    const tagKey = normalizeLookupKey(element.tagName);
    return tagKey.includes("paatale") || tagKey.includes("berettig");
  });

  for (const candidate of candidates) {
    const roleReference = normalizeWhitespace(
      candidate.getElementsByTagName("RolleReference")[0]?.textContent,
    );
    if (roleReference) {
      const role = context.rolesByReference.get(roleReference);
      if (role) {
        blocks.add(formatRolePaataleBlock(role));
      }
    }

    const candidateName = normalizeWhitespace(
      candidate.getElementsByTagName("Navn")[0]?.textContent,
    );
    if (candidateName) {
      const role = findRoleByName(context, candidateName);
      if (role) {
        blocks.add(formatRolePaataleBlock(role));
      }
    }

    const propertyInfo = extractPropertyInfoFromElement(candidate, context);
    if (propertyInfo) {
      blocks.add(
        formatPropertyPaataleBlock(
          propertyInfo,
          extractHerskendeEjendomValue(candidate),
        ),
      );
    }
  }

  return Array.from(blocks);
}

function extractTextFallbackPaataleBlocks(
  xmlDoc: Document,
  context: PaataleContext,
): string[] {
  const blocks = new Set<string>();
  const paragraphs = Array.from(xmlDoc.getElementsByTagName("Afsnit"))
    .map((paragraph) => normalizeWhitespace(paragraph.textContent))
    .filter(Boolean);

  for (const paragraph of paragraphs) {
    const normalizedParagraph = normalizeLookupKey(paragraph);
    if (!normalizedParagraph.includes("paatale")) {
      continue;
    }

    const tilkommerMatch = paragraph.match(/tilkommer(?:\s+alene)?\s+(.+?)(?:[.,]|$)/i);
    const candidateName = normalizeWhitespace(tilkommerMatch?.[1] || "");
    if (candidateName) {
      const role = findRoleByName(context, candidateName);
      if (role) {
        blocks.add(formatRolePaataleBlock(role));
        continue;
      }
      blocks.add(`Navn: ${candidateName}`);
      continue;
    }

    blocks.add(paragraph);
  }

  return Array.from(blocks);
}

function extractPaataleberettigede(xmlDoc: Document): string[] {
  const context = buildPaataleContext(xmlDoc);
  const blocks = new Set<string>();

  [
    ...extractPaataleRoleBlocks(xmlDoc, context),
    ...extractStructuredPaataleBlocks(xmlDoc, context),
    ...extractTextFallbackPaataleBlocks(xmlDoc, context),
  ].forEach((block) => {
    if (block) {
      blocks.add(block);
    }
  });

  return Array.from(blocks);
}

async function createServimapPdfBlob(
  servitut: ServitutLike,
  xmlDoc: Document,
): Promise<Blob> {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const leftMargin = 18;
  const rightMargin = 18;
  const topMargin = 18;
  const bottomMargin = 18;
  const valueX = 58;
  const valueWidth = pageWidth - valueX - rightMargin;
  const footerY = pageHeight - 8;
  const recordNumber = getTingBogRecordNumber(
    servitut.aktAliasId || servitut.aktId || "",
  );
  const servitutText = extractServitutText(xmlDoc, servitut) || "-";
  const paataleberettigede = extractPaataleberettigede(xmlDoc).join("\n\n") || "-";
  const generatedStamp = `Dannet af ServiMap - Viamap.net`;
  const generatedAt = new Date().toLocaleString("da-DK");
  let y = topMargin + 8;
  const logoDataUrl = await loadServimapLogoDataUrl();

  if (logoDataUrl) {
    try {
      const logoProps = doc.getImageProperties(logoDataUrl);
      const logoWidth = 34;
      const logoHeight = logoWidth * (logoProps.height / logoProps.width);
      doc.addImage(
        logoDataUrl,
        logoProps.fileType || "PNG",
        pageWidth - rightMargin - logoWidth,
        topMargin - 2,
        logoWidth,
        logoHeight,
      );
    } catch (error) {
      console.warn("[SERVIMAP] Could not add logo to PDF", error);
    }
  }

  const ensurePageSpace = (requiredHeight: number) => {
    if (y + requiredHeight <= pageHeight - bottomMargin - 10) {
      return;
    }
    doc.addPage();
    y = topMargin;
  };

  const drawMultilineField = (
    label: string,
    value: string,
    options?: { fontSize?: number },
  ) => {
    const fontSize = options?.fontSize ?? 11;
    const lines = doc.splitTextToSize(value || "-", valueWidth);
    ensurePageSpace(10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(`${label}:`, leftMargin, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(fontSize);
    let valueY = y;
    for (const line of lines) {
      const needsPageBreak = valueY + 6 > pageHeight - bottomMargin - 10;
      if (needsPageBreak) {
        doc.addPage();
        valueY = topMargin;
      }
      doc.text(line, valueX, valueY);
      valueY += 5;
    }
    y = valueY + 5;
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Servitut", pageWidth / 2, y, { align: "center" });
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(recordNumber || servitut.dokumentId || "", pageWidth / 2, y, {
    align: "center",
  });
  y += 16;

  drawMultilineField("Servitut tekst", servitutText, { fontSize: 10.5 });
  drawMultilineField("Påtaleberettiget", paataleberettigede);

  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(generatedAt, leftMargin, footerY);
    doc.text(`Side ${page} af ${totalPages}`, pageWidth - rightMargin, footerY - 4, {
      align: "right",
    });
    doc.text(generatedStamp, pageWidth - rightMargin, footerY, {
      align: "right",
    });
  }

  return doc.output("blob");
}

function buildZipFileName(propertyData: Partial<PropertyData>): string {
  const bfe = getPropertyBfe(propertyData);
  const now = new Date();
  const pad = (value: number) => `${value}`.padStart(2, "0");
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
  return buildDownloadFileName(
    `${stamp}_${bfe || "bfe"}_servitutter`,
    {
      defaultStem: "servitutter",
      defaultExtension: "zip",
      preserveSpaces: false,
    },
  );
}

async function addServitutPackageToZip(
  zip: JSZip,
  servitut: ServitutLike,
): Promise<ServitutBatchPackageResult> {
  const warnings: string[] = [];
  let createdFiles = 0;
  let mainDocumentFileName = getServitutMainDocumentFileName(servitut);

  const downloadTarget = getTingbogDownloadTarget(servitut);
  if (!downloadTarget) {
    throw new Error("No Tingbog download target available");
  }

  const mainPdf = await fetchTingbogDocumentBlob(downloadTarget);
  mainDocumentFileName = addFileToZip(zip, mainDocumentFileName, mainPdf);
  createdFiles += 1;

  if (!servitut.dokumentId?.trim()) {
    warnings.push("SERVIMAP XML mangler dokumentId.");
    return { createdFiles, warnings };
  }

  let xmlDoc: Document;
  try {
    xmlDoc = await DC.TL.DokumentTingbog(servitut.dokumentId.trim());
  } catch (error) {
    warnings.push(
      `Kunne ikke hente XML: ${error instanceof Error ? error.message : String(error)}`,
    );
    return { createdFiles, warnings };
  }

  try {
    const servimapFileName = getServimapDownloadFileName(mainDocumentFileName);
    addFileToZip(
      zip,
      servimapFileName,
      await createServimapPdfBlob(servitut, xmlDoc),
    );
    createdFiles += 1;
  } catch (error) {
    warnings.push(
      `Kunne ikke generere SERVIMAP: ${error instanceof Error ? error.message : String(error)}`,
    );
  }

  const bilagReferences = extractBilagReferences(xmlDoc).filter(
    (entry) => !entry.isLikelyGml,
  );
  if (bilagReferences.length === 0) {
    return { createdFiles, warnings };
  }

  for (let index = 0; index < bilagReferences.length; index += 1) {
    const bilag = bilagReferences[index];
    try {
      const result = await DC.TL.fetchServitutBilag(bilag.ref);
      if (!result?.data) {
        warnings.push(`Bilag ${index + 1} kunne ikke hentes.`);
        continue;
      }
      const bilagFileName = getServitutBilagDownloadFileName(
        mainDocumentFileName,
        index + 1,
        result.name || `bilag_${bilag.ref}.pdf`,
      );
      addFileToZip(zip, bilagFileName, result.data);
      createdFiles += 1;
    } catch (error) {
      warnings.push(
        `Bilag ${index + 1} kunne ikke hentes: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  return { createdFiles, warnings };
}

function stopHeaderToggle(event: ReactMouseEvent<HTMLElement>) {
  event.preventDefault();
  event.stopPropagation();
}

function BatchServimapDownloadButton(props: {
  propertyData: Partial<PropertyData>;
  hasAccessToFeature: (feature: FeatureType) => boolean;
}) {
  const [busy, setBusy] = useState(false);
  const servitutter = props.propertyData.ViamapTingBog?.servitutter || [];
  const eligibleServitutter = useMemo(
    () => servitutter.filter(isEligibleDigitalServitut),
    [servitutter],
  );

  const canUseBatchDownload =
    props.hasAccessToFeature(Feature.DownloadAktFraTinglysning) &&
    props.hasAccessToFeature(Feature.FetchNewTingbogAllow);

  if (!canUseBatchDownload) {
    return null;
  }

  const disabled = busy || eligibleServitutter.length === 0;
  const buttonTitle = disabled
    ? "Ingen digitaliserede servitutter efter 2009 på denne ejendom"
    : `Hent ${eligibleServitutter.length} digitaliserede servitutter efter 2009`;

  async function handleDownloadBatch() {
    if (busy) {
      return;
    }
    if (eligibleServitutter.length === 0) {
      message.info("Ingen digitaliserede servitutter efter 2009 på denne ejendom.");
      return;
    }

    message.progress(Localization.getText("Downloading documents"), 0);
    setBusy(true);
    try {
      const zip = new JSZip();
      const allWarnings: string[] = [];
      let includedFiles = 0;

      for (let index = 0; index < eligibleServitutter.length; index += 1) {
        const servitut = eligibleServitutter[index];
        const pct = (index / eligibleServitutter.length) * 100;
        message.progress(Localization.getText("Downloading documents"), pct);
        try {
          const result = await addServitutPackageToZip(zip, servitut);
          includedFiles += result.createdFiles;
          if (result.warnings.length > 0) {
            const recordNumber = getTingBogRecordNumber(
              servitut.aktAliasId || servitut.aktId || "",
            ) || servitut.dokumentId || "ukendt";
            result.warnings.forEach((warning) => {
              allWarnings.push(`${recordNumber}: ${warning}`);
            });
          }
        } catch (error) {
          const recordNumber = getTingBogRecordNumber(
            servitut.aktAliasId || servitut.aktId || "",
          ) || servitut.dokumentId || "ukendt";
          allWarnings.push(
            `${recordNumber}: ${error instanceof Error ? error.message : String(error)}`,
          );
        }
        message.progress(
          Localization.getText("Downloading documents"),
          ((index + 1) / eligibleServitutter.length) * 100,
        );
      }

      if (includedFiles === 0) {
        message.error("Kunne ikke danne SERVIMAP ZIP.");
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      ExportData.downloadBlob(buildZipFileName(props.propertyData), zipBlob);

      if (allWarnings.length > 0) {
        console.warn("[SERVIMAP batch] warnings", allWarnings);
      }
    } finally {
      message.progress();
      setBusy(false);
    }
  }

  return (
    <GlassButton
      title={buttonTitle}
      disabled={disabled}
      onMouseDown={stopHeaderToggle}
      onClick={(event) => {
        stopHeaderToggle(event);
        void handleDownloadBatch();
      }}
      style={{
        marginLeft: 0,
        minHeight: 28,
        height: 28,
        padding: "2px 8px",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
      }}
    >
      <MIBIcon icon="download" />
      <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>Hent servitutter ZIP</span>
    </GlassButton>
  );
}

export function TingbogServitutterSectionTitle(props: {
  propertyData: Partial<PropertyData>;
  hasAccessToFeature: (feature: FeatureType) => boolean;
  onCopyToExcel?: (event: ReactMouseEvent<HTMLElement>) => void;
}) {
  const canCopyToExcel =
    Boolean(props.onCopyToExcel) &&
    props.hasAccessToFeature(Feature.ServitutTilExcel);

  return (
    <>
      <span>Tingbog - Servitutter</span>
      <div
        style={{
          marginLeft: "auto",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.35rem",
        }}
      >
        {canCopyToExcel ? (
          <ViewButtonIcon
            title={Localization.getText("Copy to Excel")}
            onClick={(event) => {
              stopHeaderToggle(event as ReactMouseEvent<HTMLElement>);
              props.onCopyToExcel?.(event as ReactMouseEvent<HTMLElement>);
            }}
          >
            <MIBIcon icon="copy" />
          </ViewButtonIcon>
        ) : null}
        <BatchServimapDownloadButton
          propertyData={props.propertyData}
          hasAccessToFeature={props.hasAccessToFeature}
        />
      </div>
    </>
  );
}
